import React, { useEffect, useState } from "react";
import api from "../api";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const fmt = (n, suffix = "") =>
  n === null || n === undefined ? "—" : `${n}${suffix}`;
const fmtSec = (n) =>
  n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}s`;

export default function Health() {
  const [snap, setSnap] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const [n, h] = await Promise.all([
          api.get("/admin/health"),
          api.get("/admin/health/history?hours=24"),
        ]);
        if (cancelled) return;
        setSnap(n.data);
        setHistory(h.data);
      } catch {
        // handled by interceptor
      }
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="health-page">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">Health</h1>
        <div className="text-xs font-mono text-muted-foreground">
          {snap?.ts ? `Last sample ${new Date(snap.ts).toLocaleTimeString()}` : "…"}
        </div>
      </div>

      <DependencyRow snap={snap} />

      <div className="filmstrip-divider" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border">
        <Panel
          label="CPU"
          value={snap ? fmt(snap.system.cpu_pct, "%") : "—"}
          danger={snap?.system?.cpu_pct >= 85}
          history={history}
          path={(s) => s.system?.cpu_pct}
          unit="%"
        />
        <Panel
          label="RAM"
          value={snap ? fmt(snap.system.ram_pct, "%") : "—"}
          sub={
            snap
              ? `${Math.round(snap.system.ram_used_mb)} / ${Math.round(snap.system.ram_total_mb)} MB`
              : ""
          }
          danger={snap?.system?.ram_pct >= 85}
          history={history}
          path={(s) => s.system?.ram_pct}
          unit="%"
        />
        <Panel
          label="Disk"
          value={snap ? fmt(snap.system.disk_pct, "%") : "—"}
          sub={snap ? `${snap.system.disk_free_gb} GB free` : ""}
          danger={snap?.system?.disk_pct >= 90}
          history={history}
          path={(s) => s.system?.disk_pct}
          unit="%"
        />
        <Panel
          label="Queue size"
          value={snap ? snap.jobs.queue_size : "—"}
          history={history}
          path={(s) => s.jobs?.queue_size ?? 0}
        />
        <Panel
          label="Download p95"
          value={snap ? fmtSec(snap.jobs.p95_dur_sec) : "—"}
          sub={snap ? `p50 ${fmtSec(snap.jobs.p50_dur_sec)} · p99 ${fmtSec(snap.jobs.p99_dur_sec)}` : ""}
          history={history}
          path={(s) => s.jobs?.p95_dur_sec}
          unit="s"
        />
        <Panel
          label="Wait p95"
          value={snap ? fmtSec(snap.jobs.p95_wait_sec) : "—"}
          history={history}
          path={(s) => s.jobs?.p95_wait_sec}
          unit="s"
        />
        <Panel
          label="Error rate / 1h"
          value={snap ? fmt(snap.jobs.error_rate_pct, "%") : "—"}
          sub={snap ? `${snap.jobs.errors_last_hour}/${snap.jobs.jobs_last_hour} jobs` : ""}
          danger={(snap?.jobs?.error_rate_pct ?? 0) > 10}
          history={history}
          path={(s) => s.jobs?.error_rate_pct}
          unit="%"
        />
        <Panel
          label="Mongo RTT"
          value={
            snap?.mongo?.ok ? `${snap.mongo.rtt_ms} ms` : "down"
          }
          danger={!snap?.mongo?.ok}
          history={history}
          path={(s) => (s.mongo?.ok ? s.mongo?.rtt_ms : null)}
          unit="ms"
        />
      </div>
    </div>
  );
}

function DependencyRow({ snap }) {
  const items = [
    { id: "mongo", label: "MongoDB", state: snap?.mongo },
    { id: "redis", label: "Redis", state: snap?.redis },
    { id: "s3", label: "S3 storage", state: snap?.s3 },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border" data-testid="dep-row">
      {items.map((d) => {
        const loading = !snap;
        const ok = d.state?.ok;
        const tone = loading
          ? "border-border text-muted-foreground"
          : ok
            ? "border-emerald-500/40 text-emerald-400"
            : "border-red-500/40 text-red-400";
        const status = loading ? "…" : ok ? "live" : "down";
        return (
          <div
            key={d.id}
            className="bg-background p-4"
            data-testid={`dep-${d.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
                {d.label}
              </div>
              <span
                className={`text-[10px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 border ${tone}`}
              >
                {status}
              </span>
            </div>
            <div className="mt-3 font-mono text-2xl">
              {loading ? "—" : ok ? `${d.state.rtt_ms} ms` : "—"}
            </div>
            {!loading && !ok && d.state?.error && (
              <div className="mt-1 text-[10px] font-mono text-red-400 truncate">
                {d.state.error}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Panel({ label, value, sub, danger, history, path, unit }) {
  const data = (history || [])
    .map((s) => {
      const v = path(s);
      if (v === null || v === undefined) return null;
      return { t: new Date(s.ts).getTime(), v };
    })
    .filter(Boolean);

  return (
    <div
      className="bg-background p-5"
      data-testid={`health-panel-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
          {label}
        </div>
        {sub && (
          <div className="text-[10px] font-mono text-muted-foreground">
            {sub}
          </div>
        )}
      </div>
      <div
        className={`mt-3 font-mono text-3xl tracking-tight ${
          danger ? "text-red-400" : "text-foreground"
        }`}
      >
        {value}
      </div>
      <div className="mt-3 h-20 -mx-1">
        {data.length > 1 ? (
          <div className="h-full w-full">
            <ResponsiveContainer minWidth={50} minHeight={60}>
              <AreaChart data={data}>
              <defs>
                <linearGradient id={`grad-${label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--foreground))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--foreground))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke="hsl(var(--foreground))"
                strokeWidth={1}
                fill={`url(#grad-${label})`}
                dot={false}
                isAnimationActive={false}
              />
              <XAxis dataKey="t" hide />
              <YAxis hide />
              <Tooltip
                cursor={{ stroke: "hsl(var(--border))" }}
                labelFormatter={(t) => new Date(t).toLocaleTimeString()}
                formatter={(v) => [
                  `${typeof v === "number" ? v.toFixed(1) : v}${unit || ""}`,
                  label,
                ]}
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  fontSize: 11,
                  fontFamily: "JetBrains Mono",
                }}
              />
            </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-full flex items-center text-[10px] font-mono text-muted-foreground/60">
            // collecting samples…
          </div>
        )}
      </div>
    </div>
  );
}
